/**
 * Splitting a chapter at headings or scene breaks (decision 14, the long-chapter offer; sync case table, L7; part 08
 * log, principle 5, table SP). The pieces are written from the chapter's model, so each is in canonical form, and each
 * is read back before it is returned: its blocks must be the original's, and every note reference in it must resolve to
 * the same note content as before. Pure.
 */
import { indexFootnotes } from '../parser/footnote-index.ts';
import { parseChapter } from '../parser/parse.ts';
import type { ParsedChapter } from '../parser/parse.ts';
import type { Inline } from '../literal/inline.ts';
import { chapterModel } from '../literal/model.ts';
import type { Block, Footnote } from '../literal/model.ts';
import { OutputLimitError, outputLimit, serializeChapter } from '../literal/serialize.ts';
import { modelShape } from '../literal/shape.ts';

/** A place a chapter can be split: the start of a top-level heading or scene break (SP1–SP3). */
export interface SplitPoint {
  /** Where the block starts in the source. */
  readonly at: number;
  readonly kind: 'heading' | 'scene-break';
  /** The heading's text as the writer sees it; null for a scene break. */
  readonly title: string | null;
}

/** One chapter a split makes. */
export interface Piece {
  /** Its source, in canonical form. */
  readonly text: string;
  /** Its heading's text when it starts at a heading; null to keep the original's title (the first piece, SP2). */
  readonly title: string | null;
}

/** Why a split was refused (SP4, SP6, SP8, SP9, or points that aren't offered). */
export type SplitProblem = 'not-a-point' | 'empty-piece' | 'notes' | 'reads-back' | 'too-long';

/** Thrown when a split can't keep the chapter as it is (principle 5); nothing is split. */
export class SplitError extends RangeError {
  override readonly name = 'SplitError';
  readonly problem: SplitProblem;

  constructor(problem: SplitProblem, message: string) {
    super(message);
    this.problem = problem;
  }
}

/**
 * The text of inline content as the writer reads it in a title: images and note numbers left out, breaks as spaces
 * (also an imported chapter's title, part 18's C2).
 */
export function plainText(content: readonly Inline[]): string {
  let out = '';
  for (const item of content) {
    if (item.kind === 'text') for (const piece of item.pieces) out += piece.text;
    else if (item.kind === 'brackets') out += plainText(item.text);
    else if (item.kind === 'soft-break' || item.kind === 'hard-break') out += ' ';
    // What is left holds children: spans and links give their text, images and note references nothing.
    else if (item.kind !== 'image' && item.kind !== 'footnote-reference') out += plainText(item.children);
  }
  return out;
}

/** Where a chapter can be split (SP1–SP3), in source order. */
export function splitPoints(chapter: ParsedChapter): SplitPoint[] {
  const { blocks } = chapterModel(chapter);
  return pointsOf(blocks).map(({ point }) => point);
}

/** The split points of top-level `blocks`, each with its block's index. */
function pointsOf(blocks: readonly Block[]): { readonly point: SplitPoint; readonly index: number; readonly block: Block }[] {
  const out: { point: SplitPoint; index: number; block: Block }[] = [];
  // SP2: a scene break is a point only with text after it; notes alone would make a piece with no text.
  const lastText = blocks.findLastIndex((block) => block.kind !== 'footnote');
  blocks.forEach((block, index) => {
    if (index === 0) return;
    if (block.kind === 'heading') {
      out.push({ index, block, point: { at: block.from, kind: 'heading', title: plainText(block.content).replace(/\s+/gu, ' ').trim() } });
    } else if (block.kind === 'scene-break' && index < lastText) {
      out.push({ index, block, point: { at: block.from, kind: 'scene-break', title: null } });
    }
  });
  return out;
}

/** Every note definition in `blocks`, nested ones included, in source order. */
function* footnotesIn(blocks: readonly Block[]): Generator<Footnote> {
  for (const block of blocks) {
    if (block.kind === 'list') {
      for (const item of block.items) yield* footnotesIn(item);
    } else if (block.kind === 'footnote') {
      yield block;
      yield* footnotesIn(block.blocks);
    } else if (block.kind === 'quote') {
      yield* footnotesIn(block.blocks);
    }
  }
}

/** A block with the starts of the original's note references inside it, in order. */
interface Node<B extends Block = Block> {
  readonly block: B;
  readonly references: readonly number[];
}

/** A chapter's note references and what each resolves to. */
interface Notes {
  /** Reference start → the definition it uses; undefined for an orphan. */
  readonly resolves: ReadonlyMap<number, Node<Footnote> | undefined>;
}

/** The notes of a parsed chapter whose model has `blocks`, and its reference starts in order. */
function notesOf(chapter: ParsedChapter, blocks: readonly Block[]): { readonly notes: Notes; readonly starts: readonly number[] } {
  const index = indexFootnotes(chapter);
  const starts = index.references.map((reference) => reference.from);
  const within = (block: Block): number[] => starts.filter((start) => start >= block.from && start < block.to);
  const definitions = new Map([...footnotesIn(blocks)].map((definition) => [definition.from, { block: definition, references: within(definition) }]));
  // Each definition of the index, as the model has it; an orphan's index, -1, finds none.
  const byIndex = index.definitions.map((definition) => definitions.get(definition.from));
  return { notes: { resolves: new Map(index.references.map((reference) => [reference.from, byIndex[reference.definition ?? -1]])) }, starts };
}

/**
 * `blocks` (top-level, in order) with the references inside each, in one pass over the sorted `starts`. Every reference
 * lies inside one: inline content lives only in leaf blocks the model keeps, never in a link reference definition. Were
 * one ever outside, it would count in the next block, and the read-back of notes would refuse the split (SP6).
 */
function nodesOf(blocks: readonly Block[], starts: readonly number[]): Node[] {
  const next = starts[Symbol.iterator]();
  let start = next.next();
  return blocks.map((block) => {
    const references: number[] = [];
    for (; !start.done && start.value < block.to; start = next.next()) references.push(start.value);
    return { block, references };
  });
}

/** The definition that `reference` resolves to, or undefined for an orphan. */
function definitionOf(notes: Notes, reference: number): Node<Footnote> | undefined {
  return notes.resolves.get(reference);
}

/**
 * The live definitions among `own` that a piece holding `roots` reaches through its references, and the definitions
 * it reaches that it holds nowhere, to copy to its end (SP5). Definitions inside `roots` stay where they stand.
 */
function reach(notes: Notes, roots: readonly Node[], own: ReadonlySet<Block>): { readonly kept: Set<Block>; readonly added: Node<Footnote>[] } {
  const present = new Set<Block>(footnotesIn(roots.map((node) => node.block)));
  const kept = new Set<Block>();
  const added: Node<Footnote>[] = [];
  const queue = roots.flatMap((node) => node.references);
  for (const reference of queue) {
    const definition = definitionOf(notes, reference);
    if (definition === undefined || present.has(definition.block)) continue;
    present.add(definition.block);
    if (own.has(definition.block)) kept.add(definition.block);
    else added.push(definition);
    queue.push(...definition.references);
  }
  return { kept, added };
}

/** One piece before it is written: the original's top-level blocks it starts with, and its title. */
interface Slice {
  readonly nodes: readonly Node[];
  readonly title: string | null;
}

/**
 * Splits `chapter` at `at`, each the start of one of its {@link splitPoints} (principle 5, SP1–SP9). Returns the
 * pieces in order, the first staying the chapter. Throws a {@link SplitError} for a start that isn't a point, or when
 * the pieces wouldn't keep the chapter as it is.
 */
export function splitChapter(chapter: ParsedChapter, at: readonly number[]): Piece[] {
  const { blocks } = chapterModel(chapter);
  const offered = new Map(pointsOf(blocks).map((entry) => [entry.point.at, entry]));
  const cuts = [...new Set(at)].sort((a, b) => a - b).map((start) => {
    const found = offered.get(start);
    if (found === undefined) throw new SplitError('not-a-point', `${String(start)} is not a split point`);
    return found;
  });
  if (cuts.length === 0) throw new SplitError('not-a-point', 'no split point given');
  const { notes, starts } = notesOf(chapter, blocks);
  const nodes = nodesOf(blocks, starts);
  // A top-level definition is live when references from the text reach it; only live ones travel (SP5). The rest
  // (duplicates, orphans, notes only other orphans use) stay where they stand, and their references count as text.
  const topLevel = new Set<Block>(blocks.filter((block) => block.kind === 'footnote'));
  const live = reach(notes, nodes.filter((node) => !topLevel.has(node.block)), topLevel).kept;
  const slices: Slice[] = [];
  const dropped = new Set<Block>();
  const kept = (to?: number): Node[] => nodes.slice(from, to).filter((node) => !dropped.has(node.block));
  let from = 0;
  let title: string | null = null;
  for (const cut of cuts) {
    // SP2: the scene break cut at goes; a heading starts the next piece.
    const next = cut.point.kind === 'scene-break' ? cut.index + 1 : cut.index;
    // A piece after the first began at a point; one begun at a heading holds it, so only one begun at a scene break can
    // have no text here.
    if (slices.length > 0 && !kept(cut.index).some((node) => node.block.kind !== 'footnote')) {
      // SP10: a piece begun at a scene break with no text before the next point runs on into it, as one cut: its notes
      // go with the next piece, a scene break chosen there goes too, and a heading there gives the piece its title.
      if (next > cut.index) dropped.add(cut.block);
      title = cut.point.title ?? title;
    } else {
      slices.push({ nodes: kept(cut.index), title });
      from = next;
      title = cut.point.title;
    }
  }
  slices.push({ nodes: kept(), title });
  return slices.map((slice, i) => {
    const own = new Set(slice.nodes.filter((node) => live.has(node.block)).map((node) => node.block));
    const { kept, added } = reach(notes, slice.nodes.filter((node) => !own.has(node.block)), own);
    const final = [...slice.nodes.filter((node) => !own.has(node.block) || kept.has(node.block)), ...added];
    if (!final.some((node) => node.block.kind !== 'footnote')) throw new SplitError('empty-piece', `piece ${String(i + 1)} would hold no text`);
    return { text: written(chapter, notes, final), title: slice.title };
  });
}

/** The canonical text of `nodes`, checked to read back as them with every reference resolving as before (SP6–SP9). */
function written(chapter: ParsedChapter, notes: Notes, nodes: readonly Node[]): string {
  const blocks = nodes.map((node) => node.block);
  let text: string;
  try {
    text = serializeChapter({ blocks }, outputLimit(chapter.text.length));
  } catch (error) {
    if (error instanceof OutputLimitError) throw new SplitError('too-long', error.message);
    throw error;
  }
  const parsed = parseChapter(text);
  const model = chapterModel(parsed);
  if (modelShape(model.blocks) !== modelShape(blocks)) throw new SplitError('reads-back', 'a piece would not read back as the chapter');
  const shape = (definition: Node<Footnote> | undefined): string | null => (definition === undefined ? null : modelShape(definition.block.blocks));
  const expected = nodes.flatMap((node) => node.references).map((reference) => shape(definitionOf(notes, reference)));
  const piece = notesOf(parsed, model.blocks);
  const actual = piece.starts.map((reference) => shape(definitionOf(piece.notes, reference)));
  if (JSON.stringify(actual) !== JSON.stringify(expected)) throw new SplitError('notes', 'a note would resolve to other content');
  return text;
}
